import React, { useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { Expense } from '../data/expenses';
import ChecklistItem from './ChecklistItem';

interface ChecklistProps {
  items: Expense[];
  onToggle: (id: string) => void;
  onEdit: (id: string) => void;
  /** Reorder the underlying list to match the given id order */
  onReorder: (ids: string[]) => void;
}

/**
 * Stable partition: unpaid bills in the user's manual order, then paid bills.
 * (No date sorting — that would throw away the user's drag-and-drop order.)
 */
function sortExpenses(expenses: Expense[]): Expense[] {
  const incomplete = expenses.filter((e) => !e.completed);
  const completed = expenses.filter((e) => e.completed);
  return [...incomplete, ...completed];
}

const Checklist: React.FC<ChecklistProps> = ({ items, onToggle, onEdit, onReorder }) => {
  const sorted = useMemo(() => sortExpenses(items), [items]);
  const sortedIds = useMemo(() => sorted.map((e) => e.id), [sorted]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = sortedIds.indexOf(active.id as string);
    const to = sortedIds.indexOf(over.id as string);
    if (from < 0 || to < 0) return;

    // Keep completed notes in the "done" group at the end: clamp the drop
    // position so dragging never interleaves paid and unpaid notes.
    const incompleteCount = sorted.filter((e) => !e.completed).length;
    const draggedIsCompleted = sorted[from].completed;
    const min = draggedIsCompleted ? incompleteCount : 0;
    const max = draggedIsCompleted ? sortedIds.length - 1 : incompleteCount - 1;
    const clampedTo = Math.max(min, Math.min(to, max));
    if (clampedTo === from) return;

    onReorder(arrayMove(sortedIds, from, clampedTo));
  };

  return (
    <div className="checklist-wrap">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortedIds} strategy={rectSortingStrategy}>
          <div className="checklist">
            {sorted.map((item, index) => (
              <ChecklistItem
                key={item.id}
                item={item}
                index={index}
                onToggle={onToggle}
                onEdit={onEdit}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {items.length === 0 && (
        <p className="empty-state">No bills yet. Tap the + button to add your first one.</p>
      )}
    </div>
  );
};

export default Checklist;
